import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { getPublicCard, PublicCardNotFound } from '../../api/userApi';
import { buildVCard, type PublicCard } from '../../lib/userDetail';
import { initials as deriveInitials } from '../../lib/formatters';
import { env } from '../../lib/env';
import { Button, Spinner } from '../../components/ui';
import './publicCard.css';

export default function PublicCardPage() {
  const { handle = '' } = useParams();
  const query = useQuery({
    queryKey: ['public-card', handle],
    queryFn: ({ signal }) => getPublicCard(handle, signal),
    enabled: handle.length > 0,
    retry: (count, err) => !(err instanceof PublicCardNotFound) && count < 2,
  });

  if (query.isLoading) {
    return (
      <main className="pcard pcard--center">
        <Spinner />
      </main>
    );
  }

  if (query.error instanceof PublicCardNotFound) {
    return (
      <main className="pcard pcard--center">
        <p>Không tìm thấy thẻ công khai này.</p>
      </main>
    );
  }

  if (query.isError || !query.data) {
    return (
      <main className="pcard pcard--center">
        <p>Không thể tải thẻ. Vui lòng thử lại sau.</p>
      </main>
    );
  }

  return <CardView pub={query.data} />;
}

function CardView({ pub }: { pub: PublicCard }) {
  const [qr, setQr] = useState<string | null>(null);
  const publicUrl = pub.handle ? `${env.portalOrigin}/u/${pub.handle}` : '';

  useEffect(() => {
    let active = true;
    if (!publicUrl) return;
    QRCode.toDataURL(publicUrl, { width: 220, margin: 1 })
      .then((url) => active && setQr(url))
      .catch(() => active && setQr(null));
    return () => {
      active = false;
    };
  }, [publicUrl]);

  function downloadVCard(): void {
    const blob = new Blob([buildVCard(pub)], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pub.handle ?? 'card'}.vcf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const monogram = pub.initials || deriveInitials(pub.name);
  const c = pub.card;

  return (
    <main className="pcard">
      <article className="pcard__card">
        <header className="pcard__head">
          {pub.photo ? (
            <img className="pcard__avatar" src={pub.photo} alt={pub.name ?? ''} />
          ) : (
            <div className="pcard__avatar pcard__avatar--mono" aria-hidden>
              {monogram}
            </div>
          )}
          <div>
            <h1 className="pcard__name">{pub.name ?? 'Người dùng'}</h1>
            {(pub.title || pub.company) && (
              <p className="pcard__role">
                {[pub.title, pub.company].filter(Boolean).join(' · ')}
              </p>
            )}
            {pub.tagline && <p className="pcard__tagline">{pub.tagline}</p>}
          </div>
        </header>

        {pub.stats.length > 0 && (
          <ul className="pcard__stats">
            {pub.stats.map((s) => (
              <li key={s.label}>
                <span className="pcard__stat-value">{s.value}</span>
                <span className="pcard__stat-label">{s.label}</span>
              </li>
            ))}
          </ul>
        )}

        <dl className="pcard__contact">
          {c.email && <Contact label="Email" href={`mailto:${c.email}`} text={c.email} />}
          {c.phone && <Contact label="Điện thoại" href={`tel:${c.phone}`} text={c.phone} />}
          {c.website && <Contact label="Website" href={c.website} text={c.website} external />}
          {c.linkedin && <Contact label="LinkedIn" href={c.linkedin} text={c.linkedin} external />}
          {(c.socials ?? []).map((s) => (
            <Contact key={s.url} label={s.platform || 'Liên kết'} href={s.url} text={s.url} external />
          ))}
          {c.address && (
            <>
              <dt>Địa chỉ</dt>
              <dd>{c.address}</dd>
            </>
          )}
        </dl>

        {c.bio && <p className="pcard__bio">{c.bio}</p>}

        {publicUrl && (
          <footer className="pcard__actions">
            {qr && <img className="pcard__qr" src={qr} alt="Mã QR danh thiếp" width={140} height={140} />}
            <div className="pcard__buttons">
              <Button type="button" onClick={downloadVCard}>
                Tải vCard
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void navigator.clipboard?.writeText(publicUrl)}
              >
                Sao chép liên kết
              </Button>
            </div>
          </footer>
        )}
      </article>
    </main>
  );
}

function Contact({
  label,
  href,
  text,
  external,
}: {
  label: string;
  href: string;
  text: string;
  external?: boolean;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <a href={href} {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}>
          {text}
        </a>
      </dd>
    </>
  );
}
